/**
 * Shared primitives for BFF cookie-mode fetch.
 *
 * In cookie mode the access token rides in an httpOnly cookie, so requests to the BFF go with
 * `credentials: 'include'` and any caller Authorization header is dropped; a 401 (expired access
 * cookie) triggers a single-flighted session refresh and one replay. Both this package's
 * `setupKeycloakFetchWrapper` (minimal) and the www_v2 app's richer `setupFetchWrapper` (which also
 * layers header injection, SpiceDB consistency, and telemetry) build on these, so the cookie-mode
 * mechanics live in exactly one place.
 */

/** Resolve fetch input to a URL, resolving relative paths against the current origin. */
export function toURL(input: RequestInfo | URL): URL {
  const urlString = input instanceof Request ? input.url : String(input)
  return new URL(
    urlString,
    typeof window !== 'undefined' ? window.location.origin : undefined,
  )
}

/**
 * True for BFF `/auth/*` routes. The refresh call is itself under `/auth/`, so these must never
 * trigger the 401 retry — retrying would recurse into the refresh.
 */
export function isAuthRoute(url: URL): boolean {
  return url.pathname.startsWith('/auth/')
}

/** Merge a request's `input` and `init` headers into one `Headers` (init wins on conflict). */
export function mergeRequestHeaders(
  input: RequestInfo | URL,
  init: RequestInit,
): Headers {
  const headers = new Headers(
    input instanceof Request ? input.headers : undefined,
  )
  if (init.headers) {
    new Headers(init.headers).forEach((value, key) => headers.set(key, value))
  }
  return headers
}

/**
 * Replay a 401'd BFF request once, after a single-flighted `refresh`, via the UNWRAPPED `fetch` (so
 * the replay can't recurse back through the wrapper). Returns the replayed response, or `null` when
 * there's nothing to retry (not a 401, or no clone) or the refresh didn't recover the session.
 *
 * @param retryClone - the request cloned BEFORE its first send (the body stream is consumed once),
 *   or `null` when a retry isn't possible (e.g. an `/auth/*` route).
 * @param refresh - single-flighted session refresh; resolves to whether the session is valid again.
 */
export async function replayOn401(
  response: Response,
  retryClone: Request | null,
  refresh: () => Promise<boolean>,
  originalFetch: typeof fetch,
): Promise<Response | null> {
  if (response.status === 401 && retryClone && (await refresh())) {
    return originalFetch(retryClone)
  }
  return null
}
